/**
 * Mobile Menu Drawer — Extracted from DashboardMainResponsive (SRP)
 * Navigation mobile drawer with full menu items
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Banknote,
  BarChart2,
  Bell,
  Book,
  Building2,
  Calendar,
  Calculator,
  ChevronRight,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FileX,
  HelpCircle,
  Headphones,
  Key,
  Landmark,
  LayoutDashboard,
  Link as LinkIcon,
  LogOut,
  Mail,
  Network,
  Package,
  Palette,
  Percent,
  Receipt,
  RefreshCw,
  Repeat,
  Scale,
  Send,
  Settings,
  ShoppingCart,
  Sparkles,
  Upload,
  UserCheck,
  Users,
  Webhook,
  X,
} from 'lucide-react';
import type { User } from '@/features/auth/types/auth.types';
import type { Tab } from './DashboardMainResponsive';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  onLogout: () => void;
}

/**
 * Collapsible toggle for Référentiels in the mobile menu
 * Mirrors the desktop sidebar ReferencesCollapsible behavior
 */
const MobileReferencesToggle: React.FC<{
  activeTab: Tab;
  onTabClick: (tab: Tab) => void;
  t: (key: string) => string;
  btnClasses: (isActive: boolean) => string;
}> = ({ activeTab, onTabClick, t, btnClasses }) => {
  const [isOpen, setIsOpen] = useState(
    activeTab === 'chart-of-accounts' || activeTab === 'tax-rates' || activeTab === 'fiscal-years'
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 pt-3 pb-1"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
        />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {t('nav.subsections.references')}
        </span>
      </button>
      {isOpen && (
        <div className="space-y-1">
          <button onClick={() => onTabClick('chart-of-accounts')} className={btnClasses(activeTab === 'chart-of-accounts')}>
            <FileSpreadsheet className="w-5 h-5" />
            <span>{t('nav.items.chartOfAccounts')}</span>
          </button>
          <button onClick={() => onTabClick('tax-rates')} className={btnClasses(activeTab === 'tax-rates')}>
            <Receipt className="w-5 h-5" />
            <span>{t('nav.items.taxRates')}</span>
          </button>
          <button onClick={() => onTabClick('fiscal-years')} className={btnClasses(activeTab === 'fiscal-years')}>
            <Calendar className="w-5 h-5" />
            <span>{t('nav.items.fiscalYears')}</span>
          </button>
        </div>
      )}
    </>
  );
};

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, user, activeTab, onTabChange, onLogout }) => {
  const isAdmin = user?.isCompanyAdmin || user?.role === 'ADMIN' || user?.role === 'OWNER';
  const { t } = useTranslation();

  const handleTabClick = (tab: Tab) => {
    onTabChange(tab);
    onClose();
  };

  if (!isOpen) return null;

  const activeClasses = 'bg-lime-100 text-teal-700 font-medium';
  const inactiveClasses = 'text-gray-700 hover:bg-teal-50';
  const btnClasses = (isActive: boolean) =>
    `w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? activeClasses : inactiveClasses}`;

  const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
    <div className="pt-4 pb-2">
      <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[60] md:hidden transition-opacity print:hidden"
        onClick={onClose}
      />

      {/* Menu drawer */}
      <div className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white z-[60] md:hidden shadow-2xl transform transition-transform duration-300 overflow-y-auto print:hidden">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-teal-500 text-white p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('nav.tooltips.menu')}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium">{user?.username}</p>
              <p className="text-xs text-white/80">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation items */}
        <nav className="p-4 space-y-1">
          {/* Dashboard */}
          <button onClick={() => handleTabClick('dashboard')} className={btnClasses(activeTab === 'dashboard')}>
            <BarChart2 className="w-5 h-5" />
            <span>{t('nav.items.dashboard')}</span>
          </button>

          {/* Facturation */}
          <SectionHeader label={t('nav.sections.billing')} />
          <button onClick={() => handleTabClick('invoices')} className={btnClasses(activeTab === 'invoices')}>
            <Receipt className="w-5 h-5" />
            <span>{t('nav.items.invoices')}</span>
          </button>
          <button onClick={() => handleTabClick('quotes')} className={btnClasses(activeTab === 'quotes')}>
            <FileText className="w-5 h-5" />
            <span>{t('nav.items.quotes')}</span>
          </button>
          <button onClick={() => handleTabClick('recurring-invoices')} className={btnClasses(activeTab === 'recurring-invoices')}>
            <Repeat className="w-5 h-5" />
            <span>{t('nav.items.recurringInvoices')}</span>
          </button>
          <button onClick={() => handleTabClick('credit-notes')} className={btnClasses(activeTab === 'credit-notes')}>
            <FileX className="w-5 h-5" />
            <span>{t('nav.items.creditNotes')}</span>
          </button>
          <button onClick={() => handleTabClick('public-links')} className={btnClasses(activeTab === 'public-links')}>
            <LinkIcon className="w-5 h-5" />
            <span>{t('nav.items.publicLinks')}</span>
          </button>

          {/* Contacts & Produits */}
          <SectionHeader label={t('nav.sections.catalog')} />
          <button onClick={() => handleTabClick('clients')} className={btnClasses(activeTab === 'clients')}>
            <UserCheck className="w-5 h-5" />
            <span>{t('nav.items.clients')}</span>
          </button>
          <button onClick={() => handleTabClick('suppliers')} className={btnClasses(activeTab === 'suppliers')}>
            <Building2 className="w-5 h-5" />
            <span>{t('nav.items.suppliers')}</span>
          </button>
          <button onClick={() => handleTabClick('products')} className={btnClasses(activeTab === 'products')}>
            <Package className="w-5 h-5" />
            <span>{t('nav.items.products')}</span>
          </button>

          {/* Achats & Dépenses */}
          <button onClick={() => handleTabClick('achats')} className={btnClasses(activeTab === 'achats' || activeTab === 'purchases' || activeTab === 'expenses')}>
            <ShoppingCart className="w-5 h-5" />
            <span>{t('nav.items.purchasesExpenses')}</span>
          </button>

          {/* Encaissements */}
          <SectionHeader label={t('nav.sections.collections')} />
          <button onClick={() => handleTabClick('reminders')} className={btnClasses(activeTab === 'reminders')}>
            <Bell className="w-5 h-5" />
            <span>{t('nav.items.reminders')}</span>
          </button>
          <button onClick={() => handleTabClick('payments/migration')} className={btnClasses(activeTab === 'payments/migration')}>
            <RefreshCw className="w-5 h-5" />
            <span>{t('nav.items.sepaMandate')}</span>
          </button>

          {/* Trésorerie */}
          <SectionHeader label={t('nav.items.treasury')} />
          <button onClick={() => handleTabClick('treasury')} className={btnClasses(activeTab === 'treasury')}>
            <Landmark className="w-5 h-5" />
            <span>{t('nav.items.bankBalances')}</span>
          </button>
          <button onClick={() => handleTabClick('treasury/budgets')} className={btnClasses(activeTab === 'treasury/budgets')}>
            <BarChart2 className="w-5 h-5" />
            <span>{t('nav.items.budgetVsActual')}</span>
          </button>
          <button onClick={() => handleTabClick('bank-reconciliation')} className={btnClasses(activeTab === 'bank-reconciliation')}>
            <FileSpreadsheet className="w-5 h-5" />
            <span>{t('nav.items.bankReconciliation')}</span>
          </button>

          {/* Comptabilité — Restructuré : 7 items + toggle Référentiels */}
          <SectionHeader label={t('nav.sections.accounting')} />
          <button onClick={() => handleTabClick('accounting')} className={btnClasses(activeTab === 'accounting')}>
            <LayoutDashboard className="w-5 h-5" />
            <span>{t('nav.items.myResult')}</span>
          </button>
          <button onClick={() => handleTabClick('accounting-entries')} className={btnClasses(activeTab === 'journal-entries' || activeTab === 'account-reconciliation' || activeTab === 'accounting-entries')}>
            <Book className="w-5 h-5" />
            <span>{t('nav.items.entryAndReconciliation')}</span>
          </button>
          <button onClick={() => handleTabClick('accounting-balance')} className={btnClasses(activeTab === 'general-balance' || activeTab === 'general-ledger' || activeTab === 'accounting-balance')}>
            <Scale className="w-5 h-5" />
            <span>{t('nav.items.balanceAndLedger')}</span>
          </button>
          <button onClick={() => handleTabClick('financial-statements')} className={btnClasses(activeTab === 'financial-statements')}>
            <BarChart2 className="w-5 h-5" />
            <span>{t('nav.items.financialStatements')}</span>
          </button>
          <button onClick={() => handleTabClick('accounting-declarations')} className={btnClasses(activeTab === 'tva' || activeTab === 'chorus-pro' || activeTab === 'accounting-exports' || activeTab === 'probatory-archive' || activeTab === 'accounting-declarations')}>
            <Percent className="w-5 h-5" />
            <span>{t('nav.items.vatAndCompliance')}</span>
          </button>
          <button onClick={() => handleTabClick('fiscal-year-closure')} className={btnClasses(activeTab === 'fiscal-year-closure')}>
            <Archive className="w-5 h-5" />
            <span>{t('nav.items.fiscalYearClosure')}</span>
          </button>
          <MobileReferencesToggle activeTab={activeTab} onTabClick={handleTabClick} t={t} btnClasses={btnClasses} />

          {/* Outils (regroupe Imports, IA, Benchmarks, Financement) */}
          <SectionHeader label={t('nav.sections.tools')} />
          <button onClick={() => handleTabClick('imports')} className={btnClasses(activeTab === 'imports' || activeTab === 'imports/history')}>
            <Upload className="w-5 h-5" />
            <span>{t('nav.items.importStatements')}</span>
          </button>
          <button onClick={() => handleTabClick('ai-actions')} className={btnClasses(activeTab === 'ai-actions')}>
            <Sparkles className="w-5 h-5" />
            <span>{t('nav.items.aiActions')}</span>
          </button>
          <button onClick={() => handleTabClick('benchmarks')} className={btnClasses(activeTab === 'benchmarks')}>
            <Network className="w-5 h-5" />
            <span>{t('nav.items.benchmarks')}</span>
          </button>
          <button onClick={() => handleTabClick('financing')} className={btnClasses(activeTab === 'financing')}>
            <Banknote className="w-5 h-5" />
            <span>{t('nav.items.financing')}</span>
          </button>

          {/* Administration unifiee (Admin only) */}
          {isAdmin && (
            <>
              <SectionHeader label={t('nav.sections.admin')} />
              <button onClick={() => handleTabClick('users')} className={btnClasses(activeTab === 'users')}>
                <Users className="w-5 h-5" />
                <span>{t('nav.items.users')}</span>
              </button>
              <button onClick={() => handleTabClick('companies')} className={btnClasses(activeTab === 'companies')}>
                <Building2 className="w-5 h-5" />
                <span>{t('nav.items.companies')}</span>
              </button>
              <button onClick={() => handleTabClick('templates')} className={btnClasses(activeTab === 'templates')}>
                <Palette className="w-5 h-5" />
                <span>{t('nav.items.docTemplates')}</span>
              </button>
              <button onClick={() => handleTabClick('email-templates')} className={btnClasses(activeTab === 'email-templates')}>
                <Mail className="w-5 h-5" />
                <span>{t('nav.items.emailTemplates')}</span>
              </button>
              <button onClick={() => handleTabClick('signatures')} className={btnClasses(activeTab === 'signatures')}>
                <FileSignature className="w-5 h-5" />
                <span>{t('nav.items.signatures')}</span>
              </button>
              <button onClick={() => handleTabClick('legal-mentions')} className={btnClasses(activeTab === 'legal-mentions')}>
                <FileText className="w-5 h-5" />
                <span>{t('nav.items.legalMentions')}</span>
              </button>
              <button onClick={() => handleTabClick('accounting-connectors')} className={btnClasses(activeTab === 'accounting-connectors')}>
                <LinkIcon className="w-5 h-5" />
                <span>{t('nav.items.accountingConnectors')}</span>
              </button>
              <button onClick={() => handleTabClick('api-keys')} className={btnClasses(activeTab === 'api-keys')}>
                <Key className="w-5 h-5" />
                <span>{t('nav.items.apiKeys')}</span>
              </button>
              <button onClick={() => handleTabClick('webhooks')} className={btnClasses(activeTab === 'webhooks')}>
                <Webhook className="w-5 h-5" />
                <span>{t('nav.items.webhooks')}</span>
              </button>
              <button onClick={() => handleTabClick('email-logs')} className={btnClasses(activeTab === 'email-logs')}>
                <Mail className="w-5 h-5" />
                <span>{t('nav.items.emailLogs')}</span>
              </button>
              <button onClick={() => handleTabClick('audit-logs')} className={btnClasses(activeTab === 'audit-logs')}>
                <Book className="w-5 h-5" />
                <span>{t('nav.items.auditLog')}</span>
              </button>
            </>
          )}

          {/* Paramètres */}
          <SectionHeader label={t('nav.sections.settings')} />
          <button onClick={() => handleTabClick('settings')} className={btnClasses(activeTab === 'settings' || activeTab.startsWith('settings/'))}>
            <Settings className="w-5 h-5" />
            <span>{t('nav.items.settings')}</span>
          </button>

          {/* Aide */}
          <SectionHeader label={t('nav.sections.help')} />
          <button onClick={() => handleTabClick('support')} className={btnClasses(activeTab === 'support')}>
            <Headphones className="w-5 h-5" />
            <span>{t('nav.items.support')}</span>
          </button>
          <button onClick={() => handleTabClick('help')} className={btnClasses(activeTab === 'help')}>
            <HelpCircle className="w-5 h-5" />
            <span>{t('nav.items.helpCenter')}</span>
          </button>
        </nav>

        {/* Footer */}
        <div className="sticky bottom-0 p-4 pb-20 md:pb-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 active:bg-red-200 transition-colors font-medium"
          >
            <LogOut className="w-5 h-5" />
            {t('nav.items.logout')}
          </button>
        </div>
      </div>
    </>
  );
};
